
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Volume2, VolumeX, Eraser, Delete, Space, Sparkles, Edit3, Fingerprint, Loader2, Check, ChevronDown, ChevronUp, RefreshCw, Pen, Send, ShieldAlert, Smartphone, ChevronLeft, ChevronRight, Music, Music2, RotateCcw, Download, Mic, MicOff, Radio } from 'lucide-react';
import { UserProfile, Mantra } from '../types';
import { getMantras } from '../services/mockService';
import { sendAIMessage } from '../services/aiService';
import { useLanguage } from './LanguageContext';
import { GuestSignupPrompt } from './GuestSignupPrompt';
import { useMusic } from '../context/MusicContext';
import { isNativePlatform, startNativeSpeech, stopNativeSpeech } from '../services/nativeSpeech';
import { scheduleNativeJaapIncomplete, cancelNativeJaapIncomplete } from '../services/nativeAlarmBridge';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { API_URL } from '../services/apiConfig';

interface Props {
  user: UserProfile;
  onUpdateUser: (u: UserProfile) => Promise<void>;
  onLogout?: () => void; // For guest to sign up (logout and show auth)
}

type JaapMode = 'likhit' | 'smaran' | 'lekhani' | 'vani';

// Type for per-mode counts and histories
type ModeData<T> = { type: T; lekhani: T; smaran: T; vani: T };
type PerModeCounts = Record<string, ModeData<number>>;
type PerModeHistories = Record<string, ModeData<string[]>>;

// Helper to get mode key from JaapMode
const getModeKey = (mode: JaapMode): keyof ModeData<any> => {
  if (mode === 'likhit') return 'type';
  if (mode === 'lekhani') return 'lekhani';
  if (mode === 'vani') return 'vani';
  return 'smaran';
};

// Default empty mode data
const emptyModeData = <T>(defaultValue: T): ModeData<T> => ({
  type: defaultValue,
  lekhani: defaultValue,
  smaran: defaultValue,
  vani: defaultValue
});

export const JaapInterface: React.FC<Props> = ({ user, onUpdateUser, onLogout }) => {
  const [mantras, setMantras] = useState<Mantra[]>([]);
  const [selectedMantraId, setSelectedMantraId] = useState('ram');
  
  // Persist notepad text in localStorage
  const [inputText, setInputText] = useState(() => {
    try {
      return localStorage.getItem(`jaap_notepad_${user.id}`) || '';
    } catch {
      return '';
    }
  });
  const [mode, setMode] = useState<JaapMode>('likhit');
  
  // For swipeable ledger - which mode's ledger is currently visible
  const [ledgerViewMode, setLedgerViewMode] = useState<keyof ModeData<any>>('type');
  
  // Save notepad text when it changes
  useEffect(() => {
    localStorage.setItem(`jaap_notepad_${user.id}`, inputText);
  }, [inputText, user.id]);
  
  const { t } = useLanguage();
  const { isPlaying: isMusicPlaying, toggle: toggleMusic } = useMusic();

  // Guest signup prompt state
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const isGuest = user.isGuest === true;

  // Prasadam / Blessing State
  const [isManifesting, setIsManifesting] = useState(false);
  const [lastBlessingStatus, setLastBlessingStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  // Lekhani (Handwriting) State
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [pendingVerifications, setPendingVerifications] = useState(0); // Track active AI checks
  
  // Accumulator for complex mantras
  const keywordBufferRef = useRef<number>(0); 

  // ====== VANI (Voice Jaap) State ======
  const [isListening, setIsListening] = useState(false);
  const [vaniTranscript, setVaniTranscript] = useState('');
  const [vaniSessionCount, setVaniSessionCount] = useState(0);
  const [vaniDetectedWords, setVaniDetectedWords] = useState<string[]>([]);
  const [vaniMode, setVaniMode] = useState<'fast' | 'accurate'>('fast');
  const recognitionRef = useRef<any>(null);
  const vaniCountRef = useRef(0);
  const restartTimeoutRef = useRef<any>(null);
  const recentFinalTextsRef = useRef<Set<string>>(new Set());
  const dedupClearTimerRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const whisperStreamRef = useRef<MediaStream | null>(null);
  const whisperIntervalRef = useRef<any>(null);
  const whisperChunksRef = useRef<Blob[]>([]);
  
  // Anti-Cheat State
  const [velocityWarning, setVelocityWarning] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  
  const lastJaapTimeRef = useRef<number>(0);
  const isInitialMount = useRef(true);

  // Session counts - persist in localStorage to survive page refresh
  // NEW STRUCTURE: {mantraId: {type: count, lekhani: count, smaran: count}}
  const [sessionCounts, setSessionCounts] = useState<PerModeCounts>(() => {
    try {
      const saved = localStorage.getItem(`jaap_session_counts_v2_${user.id}`);
      if (saved) {
        return JSON.parse(saved);
      }
      // Migration: Check if old format exists
      const oldSaved = localStorage.getItem(`jaap_session_counts_${user.id}`);
      if (oldSaved) {
        const oldData = JSON.parse(oldSaved);
        // Migrate old single counts to 'type' mode
        const migrated: PerModeCounts = {};
        Object.keys(oldData).forEach(mantraId => {
          migrated[mantraId] = { type: oldData[mantraId] || 0, lekhani: 0, smaran: 0 };
        });
        return migrated;
      }
      return {};
    } catch {
      return {};
    }
  });
  
  // Session histories - NEW STRUCTURE: {mantraId: {type: [], lekhani: [], smaran: []}}
  const [sessionHistories, setSessionHistories] = useState<PerModeHistories>(() => {
    try {
      const saved = localStorage.getItem(`jaap_session_histories_v2_${user.id}`);
      if (saved) {
        return JSON.parse(saved);
      }
      // Migration: Check if old format exists
      const oldSaved = localStorage.getItem(`jaap_session_histories_${user.id}`);
      if (oldSaved) {
        const oldData = JSON.parse(oldSaved);
        // Migrate old single histories to 'type' mode
        const migrated: PerModeHistories = {};
        Object.keys(oldData).forEach(mantraId => {
          migrated[mantraId] = { type: oldData[mantraId] || [], lekhani: [], smaran: [] };
        });
        return migrated;
      }
      return {};
    } catch {
      return {};
    }
  });
  
  // Save session counts to localStorage when they change (new v2 format)
  useEffect(() => {
    if (Object.keys(sessionCounts).length > 0) {
      localStorage.setItem(`jaap_session_counts_v2_${user.id}`, JSON.stringify(sessionCounts));
    }
  }, [sessionCounts, user.id]);
  
  useEffect(() => {
    if (Object.keys(sessionHistories).length > 0) {
      localStorage.setItem(`jaap_session_histories_v2_${user.id}`, JSON.stringify(sessionHistories));
    }
  }, [sessionHistories, user.id]);
  
  // Sync ledger view mode with active jaap mode
  useEffect(() => {
    setLedgerViewMode(getModeKey(mode));
  }, [mode]);
  
  const [isMuted, setIsMuted] = useState(false);
  const [isTapping, setIsTapping] = useState(false);
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number }[]>([]);
  
  // Volume Button Jaap State
  const [volumeButtonEnabled, setVolumeButtonEnabled] = useState(() => {
    const saved = localStorage.getItem('volumeButtonJaap');
    return saved === 'true';
  });
  
  const chimeAudioCtxRef = useRef<AudioContext | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ledgerContainerRef = useRef<HTMLDivElement>(null);

  const activeMantraRef = useRef<Mantra | null>(null);

  useEffect(() => {
    setMantras(getMantras());
  }, []);

  const activeMantra = useMemo(() => {
    const found = mantras.find(m => m.id === selectedMantraId) || mantras[0] || null;
    activeMantraRef.current = found;
    return found;
  }, [mantras, selectedMantraId]);
  
  // Get current mode key
  const currentModeKey = getModeKey(mode);
  
  // Get history for current mantra and CURRENT MODE
  const currentSessionHistory = sessionHistories[selectedMantraId]?.[currentModeKey] || [];
  // Use history length as the source of truth for count (avoids sync issues)
  const currentSessionCount = currentSessionHistory.length;
  
  // Get history for ledger view mode (can be different from active mode when swiping)
  const ledgerViewHistory = sessionHistories[selectedMantraId]?.[ledgerViewMode] || [];
  const ledgerViewCount = ledgerViewHistory.length;
  
  // Total across all modes for this mantra (based on history lengths)
  const totalAllModesCount = (sessionHistories[selectedMantraId]?.type || []).length + 
                             (sessionHistories[selectedMantraId]?.lekhani || []).length + 
                             (sessionHistories[selectedMantraId]?.smaran || []).length +
                             (sessionHistories[selectedMantraId]?.vani || []).length;
  
  // Mode labels for display
  const modeLabels: Record<keyof ModeData<any>, string> = {
    type: '✏️ TYPE',
    lekhani: '✍️ LEKHANI', 
    smaran: '🧘 SMARAN',
    vani: '🎙️ VANI'
  };

  // Reset current mala progress (without affecting total count) - MODE SPECIFIC
  // Removes entries until count is at last completed mala boundary
  const resetMala = () => {
    const modeKey = getModeKey(mode);
    const currentHistory = sessionHistories[selectedMantraId]?.[modeKey] || [];
    const currentCount = currentHistory.length;
    const completedMalas = Math.floor(currentCount / 108);
    const targetCount = completedMalas * 108; // Reset to last completed mala
    
    // Remove excess entries from history
    setSessionHistories(prev => ({
      ...prev,
      [selectedMantraId]: {
        ...(prev[selectedMantraId] || emptyModeData<string[]>([])),
        [modeKey]: currentHistory.slice(0, targetCount)
      }
    }));
    
    // Also update sessionCounts to stay in sync
    setSessionCounts(prev => ({
      ...prev,
      [selectedMantraId]: {
        ...(prev[selectedMantraId] || emptyModeData(0)),
        [modeKey]: targetCount
      }
    }));
    
    // Haptic feedback
    if (navigator.vibrate) navigator.vibrate(50);
  };

  // Download Sacred Ledger as image - MODE SPECIFIC
  const downloadLedger = async (modeToDownload: keyof ModeData<any> = ledgerViewMode) => {
    const html2canvas = (await import('html2canvas')).default;
    
    const historyToDownload = sessionHistories[selectedMantraId]?.[modeToDownload] || [];
    
    if (historyToDownload.length === 0) return;
    
    // Use the actual history length for display to ensure accuracy
    const actualCount = historyToDownload.length;
    const actualMalas = Math.floor(actualCount / 108);
    
    // Create a temporary container for the downloadable image
    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = `
      position: fixed;
      left: -9999px;
      top: 0;
      width: 400px;
      padding: 20px;
      background: linear-gradient(135deg, #fffdf5 0%, #fef3c7 100%);
      font-family: 'Noto Serif Devanagari', serif;
    `;
    
    const date = new Date().toLocaleDateString('hi-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    
    const modeLabel = modeLabels[modeToDownload];
    
    // Create content with proper wrapping - each entry as inline-block for proper flow
    const entriesHtml = historyToDownload.map(text => 
      `<span style="display: inline-block; margin-right: 10px; margin-bottom: 2px;">${text}</span>`
    ).join('');
    
    // Calculate number of lines needed (approximately 10 entries per line at 400px width)
    const entriesPerLine = 10;
    const numLines = Math.ceil(historyToDownload.length / entriesPerLine);
    const lineHeight = 32; // pixels per line
    const contentHeight = Math.max(100, numLines * lineHeight + 20);
    
    // Generate horizontal ruled lines
    const ruledLinesHtml = Array.from({ length: numLines + 2 }, (_, i) => 
      `<div style="position: absolute; left: 40px; right: 10px; top: ${(i + 1) * lineHeight}px; height: 1px; background: #e5e7eb;"></div>`
    ).join('');
    
    // Logo URL (public folder)
    const logoUrl = `${window.location.origin}/golok-logo.png`;
    
    tempContainer.innerHTML = `
      <div style="text-align: center; padding-bottom: 15px; border-bottom: 2px solid #f97316; margin-bottom: 15px;">
        <img src="${logoUrl}" alt="Golok" style="height: 50px; margin-bottom: 8px;" crossorigin="anonymous" />
        <div style="font-size: 20px; font-weight: 700; color: #c2410c;">Sacred Ledger</div>
        <div style="font-size: 13px; color: #ea580c; margin-top: 4px; font-weight: 600;">${modeLabel}</div>
        <div style="font-size: 11px; color: #92400e; margin-top: 4px;">${date}</div>
      </div>
      
      <div style="background: #fffdf5; border: 1px solid #fed7aa; border-radius: 12px; padding: 15px; position: relative; min-height: ${contentHeight}px; overflow: hidden;">
        <!-- Margin lines (vertical red lines) -->
        <div style="position: absolute; left: 28px; top: 0; bottom: 0; width: 1px; background: rgba(239, 68, 68, 0.3);"></div>
        <div style="position: absolute; left: 32px; top: 0; bottom: 0; width: 1px; background: rgba(239, 68, 68, 0.3);"></div>
        
        <!-- Horizontal ruled lines -->
        ${ruledLinesHtml}
        
        <!-- Content with flex wrap -->
        <div style="padding-left: 25px; font-size: 15px; line-height: ${lineHeight}px; color: #78350f; font-family: 'Noto Serif Devanagari', serif; display: flex; flex-wrap: wrap; align-content: flex-start; position: relative; z-index: 1;">
          ${entriesHtml}
        </div>
      </div>
      
      <div style="text-align: center; margin-top: 15px; padding-top: 15px; border-top: 1px solid #fed7aa;">
        <div style="font-size: 14px; color: #92400e; font-weight: 600;">Total: ${actualCount} जाप | ${actualMalas} माला</div>
        <div style="font-size: 11px; color: #b45309; margin-top: 8px;">गोलोक दिव्य नाम कोष</div>
        <div style="font-size: 10px; color: #d97706;">golokstore.com</div>
      </div>
    `;
    
    document.body.appendChild(tempContainer);
    
    // Wait for logo image to load before capturing
    const logoImg = tempContainer.querySelector('img');
    if (logoImg) {
      await new Promise((resolve) => {
        if (logoImg.complete) {
          resolve(true);
        } else {
          logoImg.onload = () => resolve(true);
          logoImg.onerror = () => resolve(false); // Continue even if logo fails
        }
      });
    }
    
    try {
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: '#fffdf5',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      
      const link = document.createElement('a');
      link.download = `sacred_ledger_${modeToDownload}_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      if (navigator.vibrate) navigator.vibrate(100);
    } catch (err) {
      console.error('Error downloading ledger:', err);
    } finally {
      document.body.removeChild(tempContainer);
    }
  };

  // Reset accumulation buffer when mantra changes
  useEffect(() => {
    keywordBufferRef.current = 0;
    setPendingVerifications(0);
    // Clear canvas if in lekhani mode
    if (mode === 'lekhani') clearCanvas();
  }, [selectedMantraId]);

  useEffect(() => {
    if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
    }
    if (mode === 'likhit' && inputRef.current && !isLocked) {
        inputRef.current.focus({ preventScroll: true });
    }
    if (mode === 'lekhani') {
        // Short delay to ensure canvas is rendered before sizing
        setTimeout(initCanvas, 100);
    }
  }, [selectedMantraId, mode, isLocked]);

  useEffect(() => {
    if (ledgerContainerRef.current) {
      ledgerContainerRef.current.scrollTo({
        top: ledgerContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [currentSessionHistory]);

  // Screen Edge Jaap & Keyboard Volume Keys
  // Reference for debouncing
  const volumeDebounceRef = useRef<number>(0);

  useEffect(() => {
    if (mode !== 'smaran' || !volumeButtonEnabled || isLocked) return;

    // Function to trigger jaap count (for keyboard volume keys on desktop)
    const triggerVolumeJaap = () => {
      const now = Date.now();
      // Debounce to prevent double triggers
      if (now - volumeDebounceRef.current < 200) return;
      volumeDebounceRef.current = now;

      // Trigger visual feedback
      setIsTapping(true);
      setTimeout(() => setIsTapping(false), 150);
      
      // Add ripple effect at center
      const newRipple = { id: Date.now(), x: 120, y: 120 };
      setRipples(prev => [...prev, newRipple]);
      setTimeout(() => setRipples(prev => prev.filter(r => r.id !== newRipple.id)), 800);
      
      // Increment count
      incrementCountRef.current('smaran');
    };

    // Keyboard event listener (works on desktop with keyboard volume keys)
    const handleVolumeKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'AudioVolumeUp' || e.key === 'AudioVolumeDown' || 
          e.key === 'VolumeUp' || e.key === 'VolumeDown' ||
          e.code === 'AudioVolumeUp' || e.code === 'AudioVolumeDown' ||
          e.keyCode === 175 || e.keyCode === 174 || e.keyCode === 173) {
        e.preventDefault();
        e.stopPropagation();
        triggerVolumeJaap();
      }
    };

    window.addEventListener('keydown', handleVolumeKeyDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleVolumeKeyDown, { capture: true });
    };
  }, [mode, volumeButtonEnabled, isLocked]);

  // Save volume button preference
  useEffect(() => {
    localStorage.setItem('volumeButtonJaap', volumeButtonEnabled.toString());
  }, [volumeButtonEnabled]);

  // ─── Jaap Incomplete Detection ─────────────────────────────
  // When user leaves app mid-mala → schedule native reminder
  // When user comes back or completes mala → cancel it
  useEffect(() => {
    const checkAndSchedule = () => {
      const mantra = activeMantraRef.current;
      if (!mantra) return;
      const count = currentSessionCount;
      const isIncomplete = count > 0 && count % 108 !== 0;

      if (document.hidden && isIncomplete) {
        // User left the app with incomplete mala → schedule reminder
        const soundMap: Record<string, string> = {
          'ram': 'mantra_ram', 'radha': 'mantra_radha', 'mahamantra': 'mantra_mahamantra',
        };
        const soundName = soundMap[mantra.id] || 'mantra_ram';
        scheduleNativeJaapIncomplete(10, mantra.hindiLabel || mantra.heading, soundName);
      } else if (!document.hidden) {
        // User is back → cancel reminder
        cancelNativeJaapIncomplete();
      }
    };

    document.addEventListener('visibilitychange', checkAndSchedule);

    // On mount: cancel any existing jaap incomplete alarm (user is back)
    cancelNativeJaapIncomplete();

    return () => {
      document.removeEventListener('visibilitychange', checkAndSchedule);
      // On unmount: check if mala incomplete
      const mantra = activeMantraRef.current;
      if (mantra) {
        if (currentSessionCount > 0 && currentSessionCount % 108 !== 0) {
          const soundMap: Record<string, string> = {
            'ram': 'mantra_ram', 'radha': 'mantra_radha', 'mahamantra': 'mantra_mahamantra',
          };
          const soundName = soundMap[mantra.id] || 'mantra_ram';
          scheduleNativeJaapIncomplete(10, mantra.hindiLabel || mantra.heading, soundName);
        }
      }
    };
  }, [activeMantra, mode, currentSessionCount]);

  // --- LEKHANI (Handwriting) LOGIC ---
  const initCanvas = () => {
      const canvas = canvasRef.current;
      if (canvas) {
          // Set canvas size to match display size (1:1 mapping for accurate touch)
          const rect = canvas.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = rect.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.lineWidth = 4;
          }
      }
  };

  const getPointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (isLocked) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Capture pointer for consistent tracking
    canvas.setPointerCapture(e.pointerId);
    
    // Prevent default to stop page scroll
    e.preventDefault();
    e.stopPropagation();
    
    const { x, y } = getPointerPos(e);
    
    // Store the start position - don't begin drawing until movement threshold is met
    (canvas as any).startX = x;
    (canvas as any).startY = y;
    (canvas as any).drawStarted = false;
    
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(x, y);
    
    // Store last point for smooth drawing
    (canvas as any).lastX = x;
    (canvas as any).lastY = y;
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || isLocked) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Prevent default
    e.preventDefault();
    e.stopPropagation();
    
    const { x, y } = getPointerPos(e);
    
    // Movement threshold: require 3px of movement before drawing starts
    // This prevents accidental drawing from scroll/tap gestures
    if (!(canvas as any).drawStarted) {
      const dx = x - ((canvas as any).startX ?? x);
      const dy = y - ((canvas as any).startY ?? y);
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 3) return;
      (canvas as any).drawStarted = true;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get pressure (0.5 default for mouse/touch without pressure)
    const pressure = e.pressure > 0 ? e.pressure : 0.5;
    
    // Calculate line width based on pressure (3-6 range)
    const baseWidth = 3;
    const pressureWidth = baseWidth + (pressure * 3);
    
    // Smooth line drawing using quadratic curves
    const lastX = (canvas as any).lastX ?? x;
    const lastY = (canvas as any).lastY ?? y;
    
    const midX = (lastX + x) / 2;
    const midY = (lastY + y) / 2;
    
    ctx.lineWidth = pressureWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = document.documentElement.classList.contains('dark') ? '#FF9933' : '#A0522D';
    
    ctx.quadraticCurveTo(lastX, lastY, midX, midY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(midX, midY);
    
    // Update last point
    (canvas as any).lastX = x;
    (canvas as any).lastY = y;
    
    setHasDrawn(true);
  };

  const stopDrawing = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (e) {
      e.preventDefault();
      // Release pointer capture
      const canvas = canvasRef.current;
      if (canvas && e.pointerId) {
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {}
      }
    }
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx?.closePath();
        // Clear tracking state
        (canvas as any).lastX = undefined;
        (canvas as any).lastY = undefined;
        (canvas as any).startX = undefined;
        (canvas as any).startY = undefined;
        (canvas as any).drawStarted = false;
    }
  };

  const clearCanvas = () => {
      const canvas = canvasRef.current;
      if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx?.clearRect(0, 0, canvas.width, canvas.height);
          setHasDrawn(false);
      }
  };

  // Optimistic verification: Count first, verify later
  const handleArpan = () => {
      if (!hasDrawn || isLocked) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const currentMantra = activeMantraRef.current;
      if (!currentMantra) return;

      // 1. Capture Data for Async Verification
      const base64Data = canvas.toDataURL('image/jpeg', 0.5).split(',')[1];

      // 2. Guaranteed +1 count for Lekhani (no rollback)
      incrementCount('lekhani', 1, true); 
      setPendingVerifications(prev => prev + 1);
      
      // 3. Clear UI
      canvas.style.transition = 'opacity 0.4s';
      canvas.style.opacity = '0';
      setTimeout(() => {
          clearCanvas();
          canvas.style.opacity = '1';
      }, 400);

      // 4. Background Verification (additive only - can add MORE if multiple names detected, never subtract)
      verifyHandwriting(base64Data, currentMantra);
  };

  const verifyHandwriting = async (base64Data: string, mantra: Mantra) => {
      try {
          const prompt = `Analyze the handwritten text in this image.
          Target Mantra: "${mantra.label}" (Hindi: "${mantra.hindiLabel}").
          
          Strict Task:
          1. READ the handwritten text.
          2. COMPARE it strictly against the Target Mantra.
          3. If the text is "${mantra.label}" or "${mantra.hindiLabel}" (or close spelling variations like "Rama" for "Ram"), count it.
          4. If the text is a COMPLETELY DIFFERENT word (e.g. User wrote "Radha" but Target is "Ram"), COUNT MUST BE 0.
          
          Return JSON Object ONLY: { "count": number, "detectedText": string }
          If unreadable or invalid, count should be 0.`;

          const responseText = await sendAIMessage(
            prompt,
            'You are a handwriting verification system. Always return valid JSON only.',
            `verify_${Date.now()}`,
            base64Data.includes(',') ? base64Data : `data:image/jpeg;base64,${base64Data}`,
            'image/jpeg'
          );

          const result = JSON.parse(responseText || '{"count": 0, "detectedText": ""}');
          const count = result.count || 0;
          const recognizedText = result.detectedText || "";

          // Guaranteed +1 already credited. AI can only ADD more, never subtract.
          // If count > 1: Multiple names detected, add the extras.
          // If count <= 1: Keep the guaranteed +1, no change needed.
          
          if (count > 1) {
              const extra = count - 1;
              incrementCountRef.current('lekhani', extra, true); 
              console.log(`✨ +${count} Detected! (${extra} extra added)`);
          } else if (count === 0) {
              // AI couldn't read but we keep the guaranteed +1
              console.log(`✨ Auto-Accepted (AI unreadable, kept +1)`);
          }
      } catch (err: any) {
          console.error("Background Verification Failed", err);
          
          // Check for Quota/Rate Limit errors (429) or Server Overload
          const isQuotaError = 
            err.status === 429 || 
            (err.message && (err.message.includes('429') || err.message.toLowerCase().includes('quota') || err.message.includes('RESOURCE_EXHAUSTED')));

          // If the AI service is down or quota exceeded, we DO NOT punish the user.
          // We stick with the optimistic count (+1). 
          // We show a message indicating "Auto-Accepted" or "High Traffic".
          
          if (isQuotaError) {
             console.log("✨ High Traffic: Accepted");
          } else {
             console.log("✨ Auto-Verified"); 
          }
      } finally {
          setPendingVerifications(prev => Math.max(0, prev - 1));
      }
  };

  // ====== VANI (Voice Jaap) - Accurate Engine (Final-Only + Word-Boundary) ======

  // Separate single-word and multi-word mantra keywords
  const { singleWords, multiWords } = useMemo(() => {
    const mantra = activeMantra;
    if (!mantra) return { singleWords: [] as string[], multiWords: [] as string[] };
    const keywords: string[] = [];
    const variations: Record<string, string[]> = {
      'ram': ['राम', 'रम', 'ram', 'raam', 'rama', 'श्री राम', 'श्रीराम', 'shri ram', 'shree ram', 'जय श्री राम', 'jai shri ram'],
      'sita_ram': ['सीता राम', 'सीताराम', 'sita ram', 'sitaram', 'siya ram', 'सिया राम'],
      'jai_shri_ram': ['जय श्री राम', 'जय श्रीराम', 'jai shri ram', 'jay shri ram'],
      'hare_krishna': ['हरे कृष्ण', 'hare krishna', 'हरे राम', 'hare rama'],
      'om_namah_shivaya': ['ॐ नमः शिवाय', 'om namah shivaya', 'ओम नमः शिवाय'],
      'radhe_radhe': ['राधे राधे', 'radhe radhe', 'राधे', 'radhe'],
    };
    if (mantra.id && variations[mantra.id]) keywords.push(...variations[mantra.id]);
    if (mantra.hindiLabel && !keywords.includes(mantra.hindiLabel)) keywords.push(mantra.hindiLabel);
    if (mantra.label && !keywords.includes(mantra.label)) keywords.push(mantra.label);
    const unique = [...new Set(keywords.map(k => k.trim().toLowerCase()))].filter(k => k.length > 1);
    // Separate single-word (no spaces) and multi-word (has spaces)
    const singles = unique.filter(k => !k.includes(' '));
    const multis = unique.filter(k => k.includes(' '));
    multis.sort((a, b) => b.length - a.length); // Longest first
    return { singleWords: singles, multiWords: multis };
  }, [activeMantra]);

  // Word-boundary aware counting: prevents "राम" matching inside "रामायण"
  const countMantraInText = useCallback((text: string) => {
    if (singleWords.length === 0 && multiWords.length === 0) return 0;
    // Normalize: remove punctuation, collapse spaces
    let cleaned = text.toLowerCase().replace(/[।,.:;!?\-–—'"'""()\[\]{}\/\\0-9]/g, ' ').replace(/\s+/g, ' ').trim();
    let count = 0;

    // Step 1: Match multi-word patterns first (longest first), remove matched text
    for (const mw of multiWords) {
      const escaped = mw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escaped, 'gi');
      const matches = cleaned.match(regex);
      if (matches) {
        count += matches.length;
        cleaned = cleaned.replace(regex, ' ');
      }
    }

    // Step 2: Split remaining text into words, match single-word patterns EXACTLY
    const words = cleaned.split(/\s+/).filter(w => w.length > 0);
    for (const word of words) {
      if (singleWords.includes(word)) {
        count++;
      }
    }
    return count;
  }, [singleWords, multiWords]);

  const isListeningRef = useRef(false);
  const finalTranscriptRef = useRef('');

  const startVani = useCallback(() => {
    // Use native speech recognition in Capacitor app
    if (isNativePlatform()) {
      finalTranscriptRef.current = '';
      startNativeSpeech('hi-IN', {
        onResult: (text: string, isFinal: boolean) => {
          if (isFinal && text) {
            if (!recentFinalTextsRef.current.has(text)) {
              recentFinalTextsRef.current.add(text);
              setTimeout(() => recentFinalTextsRef.current.delete(text), 4000);
              finalTranscriptRef.current += text + ' ';
              const count = countMantraInText(text);
              if (count > 0) {
                vaniCountRef.current += count;
                setVaniSessionCount(vaniCountRef.current);
                setVaniDetectedWords(prev => {
                  const newWords = [...prev, ...Array(count).fill(activeMantra?.hindiLabel || 'राम')];
                  return newWords.slice(-30);
                });
                incrementCountRef.current('vani', count);
              }
            }
            setVaniTranscript(finalTranscriptRef.current);
          } else {
            setVaniTranscript(finalTranscriptRef.current + (text ? '... ' + text : ''));
          }
        },
        onStart: () => {
          isListeningRef.current = true;
          setIsListening(true);
        },
        onEnd: () => {
          // Native handles restart internally
        },
        onError: (error: string) => {
          if (error === 'not-allowed') {
            isListeningRef.current = false;
            setIsListening(false);
            alert('माइक्रोफोन की अनुमति दें। Settings > Microphone enable करें।');
          }
        },
      });
      return;
    }

    // Fallback: Web Speech API for browsers
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('आपका browser Speech Recognition support नहीं करता। कृपया Chrome browser use करें।');
      return;
    }

    if (recognitionRef.current) {
      try { recognitionRef.current.onend = null; recognitionRef.current.abort(); } catch(e) {}
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';
    recognition.maxAlternatives = 1;

    finalTranscriptRef.current = '';

    recognition.onstart = () => {
      isListeningRef.current = true;
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let interimText = '';
      let newFinalText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const confidence = result[0].confidence || 1;
          if (confidence > 0.4) {
            const text = result[0].transcript.trim();
            // Dedup: skip if we've seen this exact text recently
            if (text && !recentFinalTextsRef.current.has(text)) {
              recentFinalTextsRef.current.add(text);
              // Auto-clear from dedup set after 4 seconds
              setTimeout(() => recentFinalTextsRef.current.delete(text), 4000);
              newFinalText += text + ' ';
            }
          }
        } else {
          interimText += result[0].transcript;
        }
      }

      // Count ONLY from final results that passed dedup
      if (newFinalText) {
        finalTranscriptRef.current += newFinalText;
        const count = countMantraInText(newFinalText);
        if (count > 0) {
          vaniCountRef.current += count;
          setVaniSessionCount(vaniCountRef.current);
          setVaniDetectedWords(prev => {
            const newWords = [...prev, ...Array(count).fill(activeMantra?.hindiLabel || 'राम')];
            return newWords.slice(-30);
          });
          incrementCountRef.current('vani', count);
        }
      }

      // Interim for visual feedback only
      setVaniTranscript(finalTranscriptRef.current + (interimText ? '... ' + interimText : ''));
    };

    recognition.onerror = (event: any) => {
      if (['no-speech', 'audio-capture', 'network', 'aborted'].includes(event.error)) {
        if (isListeningRef.current) {
          clearTimeout(restartTimeoutRef.current);
          restartTimeoutRef.current = setTimeout(() => {
            try { recognitionRef.current?.start(); } catch(e) {}
          }, 200);
        }
      } else if (event.error === 'not-allowed') {
        isListeningRef.current = false;
        setIsListening(false);
        alert('माइक्रोफोन की अनुमति दें। Settings > Microphone enable करें।');
      }
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        clearTimeout(restartTimeoutRef.current);
        // 500ms delay to let audio buffer fully clear and prevent duplicate results
        restartTimeoutRef.current = setTimeout(() => {
          if (isListeningRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch(e) {
              setTimeout(() => {
                try { recognitionRef.current?.start(); } catch(e2) {}
              }, 500);
            }
          }
        }, 500);
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch(e) {
      console.error('Failed to start recognition:', e);
    }
  }, [countMantraInText, activeMantra]);

  // Keep screen awake during Vani listening (native only)
  useEffect(() => {
    if (isNativePlatform() && isListening) {
      try { KeepAwake.keepAwake(); } catch {}
      return () => { try { KeepAwake.allowSleep(); } catch {} };
    }
  }, [isListening]);

  const stopVani = useCallback(() => {
    isListeningRef.current = false;
    setIsListening(false);
    clearTimeout(restartTimeoutRef.current);

    // Stop native speech if in Capacitor
    if (isNativePlatform()) {
      stopNativeSpeech();
      return;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
      } catch(e) {}
      recognitionRef.current = null;
    }
  }, []);

  const resetVani = useCallback(() => {
    stopVani();
    setVaniTranscript('');
    setVaniSessionCount(0);
    setVaniDetectedWords([]);
    vaniCountRef.current = 0;
    finalTranscriptRef.current = '';
    recentFinalTextsRef.current.clear();
  }, [stopVani]);

  // ====== VANI WHISPER (Accurate Mode) ======

  const sendChunkToWhisper = useCallback(async (audioBlob: Blob) => {
    if (audioBlob.size < 1000) return; // Skip tiny chunks
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'chunk.webm');
      const res = await fetch(`${API_URL}/api/vani/transcribe`, { method: 'POST', body: formData });
      if (!res.ok) return;
      const data = await res.json();
      if (data.text) {
        const text = data.text.trim();
        if (text) {
          finalTranscriptRef.current += text + ' ';
          setVaniTranscript(finalTranscriptRef.current);
          const count = countMantraInText(text);
          if (count > 0) {
            vaniCountRef.current += count;
            setVaniSessionCount(vaniCountRef.current);
            setVaniDetectedWords(prev => [...prev, ...Array(count).fill(activeMantra?.hindiLabel || 'राम')].slice(-30));
            incrementCountRef.current('vani', count);
          }
        }
      }
    } catch (err) {
      console.error('Whisper chunk error:', err);
    }
  }, [API_URL, countMantraInText, activeMantra]);

  const startVaniWhisper = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      whisperStreamRef.current = stream;
      isListeningRef.current = true;
      setIsListening(true);
      finalTranscriptRef.current = '';

      const recordChunk = () => {
        if (!isListeningRef.current || !whisperStreamRef.current) return;
        whisperChunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
        const recorder = new MediaRecorder(whisperStreamRef.current, { mimeType });
        mediaRecorderRef.current = recorder;
        
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) whisperChunksRef.current.push(e.data);
        };
        
        recorder.onstop = () => {
          if (whisperChunksRef.current.length > 0) {
            const blob = new Blob(whisperChunksRef.current, { type: mimeType });
            sendChunkToWhisper(blob);
          }
        };
        
        recorder.start(100); // collect data every 100ms
        // Stop after 5 seconds and send
        setTimeout(() => {
          if (recorder.state === 'recording') {
            recorder.stop();
          }
          // Start next chunk
          if (isListeningRef.current) {
            setTimeout(recordChunk, 200);
          }
        }, 5000);
      };
      
      recordChunk();
    } catch (err) {
      console.error('Whisper mic error:', err);
      alert('माइक्रोफोन की अनुमति दें।');
    }
  }, [sendChunkToWhisper]);

  const stopVaniWhisper = useCallback(() => {
    isListeningRef.current = false;
    setIsListening(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (whisperStreamRef.current) {
      whisperStreamRef.current.getTracks().forEach(t => t.stop());
      whisperStreamRef.current = null;
    }
  }, []);

  const resetVaniAll = useCallback(() => {
    stopVani();
    stopVaniWhisper();
    setVaniTranscript('');
    setVaniSessionCount(0);
    setVaniDetectedWords([]);
    vaniCountRef.current = 0;
    finalTranscriptRef.current = '';
    recentFinalTextsRef.current.clear();
  }, [stopVani, stopVaniWhisper]);

  // Cleanup on unmount or mode change
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.onend = null; recognitionRef.current.abort(); } catch(e) {}
      }
      clearTimeout(restartTimeoutRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (whisperStreamRef.current) {
        whisperStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Stop vani when switching away from vani mode
  useEffect(() => {
    if (mode !== 'vani' && isListening) {
      stopVani();
      stopVaniWhisper();
    }
  }, [mode, isListening, stopVani]);

  const playChime = useCallback((isMala = false) => {
    if (isMuted) return;
    try {
      if (!chimeAudioCtxRef.current) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) chimeAudioCtxRef.current = new AudioCtx();
      }
      const ctx = chimeAudioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      if (isMala) {
        osc.frequency.setValueAtTime(523.25, t); 
        osc.frequency.exponentialRampToValueAtTime(1046.50, t + 0.4); 
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.15, t + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 2.0);
        osc.stop(t + 2.0);
      } else {
        osc.frequency.setValueAtTime(440, t);
        osc.frequency.exponentialRampToValueAtTime(880, t + 0.1);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.05, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
        osc.stop(t + 0.4);
      }
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
    } catch (e) {}
  }, [isMuted]);

  const triggerSilentBlessing = async (mantra: Mantra) => {
    // Blessing animation only - no prasadam gallery save
    try {
      if (navigator.vibrate) navigator.vibrate([40, 20, 40]);
    } catch {}
  };

  const incrementCount = useCallback(async (source: 'likhit' | 'smaran' | 'vani' | 'lekhani' = 'likhit', amount = 1, force = false) => {
      const currentActiveMantra = activeMantraRef.current;
      if (!currentActiveMantra || isLocked) return;

      // Haptic feedback on each jaap count (native only)
      if (isNativePlatform() && amount > 0) {
        try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}
      }

      const now = Date.now();
      
      // Determine which mode key to update based on source
      const modeKey: keyof ModeData<any> = source === 'likhit' ? 'type' : source === 'lekhani' ? 'lekhani' : source === 'vani' ? 'vani' : 'smaran';
      
      // Manual Input Throttle (Not for Vani or Forced updates like rollbacks/multi-add)
      if (!force) {
          const timeSinceLast = now - lastJaapTimeRef.current;
          const minInterval = currentActiveMantra.id === 'mahamantra' ? 2200 : 150;
          if (timeSinceLast < minInterval) {
            if (source === 'likhit') {
               setVelocityWarning(true);
               setTimeout(() => setVelocityWarning(false), 2000);
               setInputText('');
            }
            return;
          }
      }

      if (!force) lastJaapTimeRef.current = now;

      const currentMantraCount = user.mantra_counts?.[currentActiveMantra.id] || 0;
      // Allow negative amounts for rollback
      const newMantraCounts = { ...user.mantra_counts, [currentActiveMantra.id]: Math.max(0, currentMantraCount + amount) };
      
      // Get current mode-specific count
      const currentModeCount = sessionCounts[currentActiveMantra.id]?.[modeKey] || 0;
      const nextCount = Math.max(0, currentModeCount + amount);
      
      // Update session counts for specific mode
      setSessionCounts(prev => ({
        ...prev,
        [currentActiveMantra.id]: {
          ...(prev[currentActiveMantra.id] || emptyModeData(0)),
          [modeKey]: nextCount
        }
      }));

      // Determine if a Mala (108) boundary was crossed (only for increments)
      if (amount > 0) {
          const prevCount = currentModeCount;
          const crossedMala = Math.floor(nextCount / 108) > Math.floor(prevCount / 108);
          
          if (crossedMala) {
            playChime(true);
            triggerSilentBlessing(currentActiveMantra);
            // Mala complete → cancel jaap incomplete alarm
            cancelNativeJaapIncomplete();
          } else if (source !== 'vani') {
            // Skip chime in Vani mode to prevent mic feedback loop
            playChime(false);
          }
          
          if (navigator.vibrate) {
             if (crossedMala) navigator.vibrate([80, 40, 80]);
             else navigator.vibrate(15);
          }
      }

      let entryContent = currentActiveMantra.hindiLabel;
      if (currentActiveMantra.id === 'mahamantra') {
        entryContent = "हरे कृष्ण हरे कृष्ण कृष्ण कृष्ण हरे हरे हरे राम हरे राम राम राम हरे हरे";
      }

      // Handle history updates - MODE SPECIFIC
      if (amount < 0) {
          // If rolling back 1, remove last. If rolling back more, remove N.
          const removeCount = Math.abs(amount);
          setSessionHistories(prev => ({
            ...prev,
            [currentActiveMantra.id]: {
              ...(prev[currentActiveMantra.id] || emptyModeData<string[]>([])),
              [modeKey]: (prev[currentActiveMantra.id]?.[modeKey] || []).slice(0, -removeCount)
            }
          }));
      } else {
          // Limit batch addition to avoid performance spikes
          const entriesToAdd = Math.min(amount, 50); 
          const newEntries = Array(entriesToAdd).fill(entryContent);
          
          setSessionHistories(prev => ({
            ...prev,
            [currentActiveMantra.id]: {
              ...(prev[currentActiveMantra.id] || emptyModeData<string[]>([])),
              [modeKey]: [...(prev[currentActiveMantra.id]?.[modeKey] || []), ...newEntries].slice(-300)
            }
          }));
      }
      
      // Skip database save for guest users - they can do jaap but progress won't persist
      if (user.isGuest) {
        return; // Session counts still tracked locally, just not saved
      }
      
      onUpdateUser({
        ...user,
        total_count: Math.max(0, user.total_count + amount),
        mantra_counts: newMantraCounts,
        likhit_counts: { ...(user.likhit_counts || {}), [currentActiveMantra.id]: source === 'likhit' ? Math.max(0, (user.likhit_counts?.[currentActiveMantra.id] || 0) + amount) : (user.likhit_counts?.[currentActiveMantra.id] || 0) },
        smaran_counts: { ...(user.smaran_counts || {}), [currentActiveMantra.id]: source === 'smaran' ? Math.max(0, (user.smaran_counts?.[currentActiveMantra.id] || 0) + amount) : (user.smaran_counts?.[currentActiveMantra.id] || 0) },
        vani_counts: { ...(user.vani_counts || {}), [currentActiveMantra.id]: source === 'vani' ? Math.max(0, (user.vani_counts?.[currentActiveMantra.id] || 0) + amount) : (user.vani_counts?.[currentActiveMantra.id] || 0) }
      });
  }, [user, onUpdateUser, playChime, isLocked, sessionCounts]);

  const incrementCountRef = useRef(incrementCount);
  useEffect(() => {
    incrementCountRef.current = incrementCount;
  }, [incrementCount]);


  const handleSmaranTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (isLocked) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const newRipple = { id: Date.now(), x, y };
    setRipples(prev => [...prev, newRipple]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== newRipple.id)), 800);
    setIsTapping(true);
    setTimeout(() => setIsTapping(false), 150);
    incrementCount('smaran');
  };

  const processInput = useCallback((value: string) => {
    if (!activeMantra || isLocked) return;
    const normalizedValue = value.replace(/\s+/g, ' ');
    const checkMatch = (text: string, mantra: Mantra) => {
      const result = mantra.patterns.some(p => new RegExp(p, 'i').test(text.trim()));
      console.log('Checking match:', { text: text.trim(), patterns: mantra.patterns, result });
      return result;
    };
    if (checkMatch(normalizedValue, activeMantra)) {
      incrementCount('likhit');
      setInputText(''); 
      return;
    }
    setInputText(normalizedValue);
  }, [activeMantra, incrementCount, isLocked]);

  const handleKeyClick = (key: string) => {
    if (isLocked) return;
    if (key === 'backspace') { setInputText(prev => prev.slice(0, -1)); return; }
    if (key === 'clear') { setInputText(''); return; }
    if (key === 'space') { processInput(inputText + ' '); return; }
    const newVal = inputText === '' ? key : (key.length > 2 && !inputText.endsWith(' ') ? inputText + ' ' + key : inputText + key);
    processInput(newVal);
  };

  if (!activeMantra) return null;

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-5xl mx-auto pt-1 pb-4 sm:pb-6 relative">

      {/* Guest Banner - Minimal inline */}
      {isGuest && (
        <div 
          onClick={() => setShowGuestPrompt(true)}
          className="w-full mb-2 px-4 flex justify-center cursor-pointer"
        >
          <span className="text-xs text-gray-500 dark:text-stone-500 hover:text-saffron transition-colors">
            {t('guest_mode')} • <span className="text-saffron font-medium">{t('guest_signup')}</span>
          </span>
        </div>
      )}

      {/* Guest Signup Prompt Modal */}
      <GuestSignupPrompt 
        isOpen={showGuestPrompt}
        onClose={() => setShowGuestPrompt(false)}
        onSignUp={() => {
          setShowGuestPrompt(false);
          if (onLogout) onLogout(); // Logout guest to show auth screen
        }}
        featureName="save your jaap progress"
      />

      {/* Mantra Selector - Compact & Highlighted */}
      <div className="mb-3 w-full px-3 relative" id="mantra-selector">
        {/* Left Arrow - Desktop Only */}
        <button 
          onClick={() => {
            const container = document.getElementById('mantra-scroll-container');
            if (container) container.scrollBy({ left: -150, behavior: 'smooth' });
          }}
          className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-700 rounded-full items-center justify-center shadow-md hover:bg-saffron/10 hover:border-saffron transition-all"
          data-testid="mantra-scroll-left"
        >
          <ChevronLeft size={18} className="text-saffron" />
        </button>
        
        <div id="mantra-scroll-container" className="overflow-x-auto no-scrollbar lg:mx-8">
          <div className="flex justify-center gap-2 pb-1 min-w-max">
            {mantras.map((m) => (
              <button
                key={m.id}
                onClick={() => { setSelectedMantraId(m.id); setInputText(''); }}
                data-testid={`mantra-${m.id}`}
                className={`px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wide transition-all whitespace-nowrap ${
                  selectedMantraId === m.id 
                    ? 'bg-gradient-to-r from-saffron via-orange-500 to-amber-500 text-white shadow-lg shadow-saffron/40 scale-110 font-black' 
                    : 'bg-white dark:bg-stone-800 text-gray-400 dark:text-stone-500 hover:bg-saffron/10 hover:text-saffron border border-gray-200 dark:border-stone-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Right Arrow - Desktop Only */}
        <button 
          onClick={() => {
            const container = document.getElementById('mantra-scroll-container');
            if (container) container.scrollBy({ left: 150, behavior: 'smooth' });
          }}
          className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-700 rounded-full items-center justify-center shadow-md hover:bg-saffron/10 hover:border-saffron transition-all"
          data-testid="mantra-scroll-right"
        >
          <ChevronRight size={18} className="text-saffron" />
        </button>
      </div>

      {/* Silent Mala Progress */}
      <div data-testid="jaap-stats" className="w-full max-w-md px-4 mb-4 relative">
         <div className="flex justify-between items-center text-[9px] font-black uppercase text-gray-400 tracking-widest mb-1.5 h-6">
            <span className="flex items-center gap-2">
               {/* Music Toggle Button - Made Noticeable */}
               <button
                  onClick={toggleMusic}
                  className={`relative p-2 rounded-full transition-all duration-300 active:scale-90 ${
                     isMusicPlaying 
                        ? 'bg-gradient-to-br from-saffron to-amber-500 text-white shadow-lg shadow-saffron/40' 
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-stone-700 dark:to-stone-600 text-gray-500 dark:text-stone-400 hover:from-saffron/20 hover:to-amber-100 hover:text-saffron dark:hover:from-saffron/30 dark:hover:to-amber-900/30'
                  }`}
                  title={isMusicPlaying ? 'Pause Music' : 'Play Music'}
                  data-testid="music-toggle-btn"
               >
                  {/* Glow ring when playing */}
                  {isMusicPlaying && (
                     <span className="absolute inset-0 rounded-full animate-ping bg-saffron/30"></span>
                  )}
                  {isMusicPlaying ? (
                     <Music2 size={16} className="relative z-10 animate-bounce" style={{ animationDuration: '1s' }} />
                  ) : (
                     <Music size={16} className="relative z-10" />
                  )}
               </button>
               {t('jaap_mala_progress')}
            </span>
            <div className="flex items-center gap-2">
               {isManifesting ? (
                 <div className="flex items-center gap-1.5 text-blue-500 animate-pulse px-2 rounded-full border border-blue-100 dark:border-blue-900/20">
                    <Sparkles size={10} className="animate-spin-slow" />
                    <span className="text-[8px] font-black">Weaving Art...</span>
                 </div>
               ) : lastBlessingStatus === 'success' ? (
                 <div className="flex items-center gap-1.5 text-green-500 animate-fade-in px-2 rounded-full border border-green-100 dark:border-green-900/20">
                    <Check size={10} />
                    <span className="text-[8px] font-black">Blessing Saved</span>
                 </div>
               ) : (
                 <>
                   <span className="text-saffron">{(currentSessionCount % 108)} / 108</span>
                   <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                     📿 {Math.floor(currentSessionCount / 108)} माला
                   </span>
                   {/* Reset Mala Button - only show if mala in progress */}
                   {(currentSessionCount % 108) > 0 && (
                     <button
                       onClick={resetMala}
                       className="p-1 rounded-full bg-gray-100 dark:bg-stone-700 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors group"
                       title="Reset current mala"
                       data-testid="reset-mala-btn"
                     >
                       <RotateCcw size={12} className="text-gray-400 group-hover:text-red-500 transition-colors" />
                     </button>
                   )}
                 </>
               )}
            </div>
         </div>
         
         {/* Mala Completion Reminder */}
         {(currentSessionCount % 108) > 0 && (currentSessionCount % 108) !== 0 && !isManifesting && (
           <div className="w-full max-w-md mx-auto mb-2 px-4">
             <div className="flex items-center justify-center gap-2 py-1.5 px-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-stone-800 dark:to-stone-900 rounded-full border border-amber-200 dark:border-amber-800/50">
               <span className="text-amber-600 dark:text-amber-400 text-[10px] font-bold animate-pulse">
                 🙏 {108 - (currentSessionCount % 108)} नाम और! माला पूरी करो
               </span>
             </div>
           </div>
         )}
         
         <div data-testid="mala-counter" className="w-full h-1.5 bg-gray-100 dark:bg-stone-800 rounded-full overflow-hidden shadow-inner">
            <div 
               className="h-full bg-saffron transition-all duration-300 ease-out" 
               style={{ width: `${( (currentSessionCount % 108) / 108 ) * 100}%` }}
            ></div>
         </div>
      </div>

      {/* Mantra Heading */}
      <div className="text-center px-4 mb-2 min-h-[50px] sm:min-h-[55px] flex flex-col items-center justify-center">
        <h2 className={`font-hindi font-bold text-templeRed dark:text-saffron drop-shadow-sm leading-tight ${activeMantra?.id === 'mahamantra' ? 'text-base sm:text-lg md:text-xl' : 'text-2xl sm:text-3xl md:text-4xl'}`}>
          {activeMantra.heading}
        </h2>
      </div>

      {/* Mode Toggle */}
      <div className="flex flex-col items-center w-full max-w-[480px] mb-3 space-y-1">
        <div className="flex bg-gradient-to-r from-orange-50 to-amber-50 dark:from-stone-800 dark:to-stone-800 p-0.5 rounded-full w-full border-2 border-saffron/20 dark:border-stone-600 shadow-sm">
           <button 
             onClick={() => { setMode('likhit'); setInputText(''); }}
             className={`flex-1 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1 ${mode === 'likhit' ? 'bg-gradient-to-r from-templeRed to-orange-600 text-white shadow-lg scale-105' : 'text-gray-500 dark:text-stone-400 hover:text-templeRed'}`}
           >
              <Edit3 size={12} /> <span>{t('jaap_type')}</span>
           </button>
           <button 
             onClick={() => { setMode('lekhani'); setInputText(''); }}
             className={`flex-1 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1 ${mode === 'lekhani' ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-105' : 'text-gray-500 dark:text-stone-400 hover:text-orange-500'}`}
           >
              <Pen size={12} /> <span>{t('jaap_lekhani')}</span>
           </button>
           <button 
             onClick={() => { setMode('smaran'); setInputText(''); }}
             className={`flex-1 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1 ${mode === 'smaran' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg scale-105' : 'text-gray-500 dark:text-stone-400 hover:text-green-500'}`}
           >
              <Fingerprint size={12} /> <span>{t('jaap_smaran')}</span>
           </button>
           <button 
             onClick={() => { setMode('vani'); setInputText(''); }}
             className={`flex-1 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1 ${mode === 'vani' ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg scale-105' : 'text-gray-500 dark:text-stone-400 hover:text-purple-500'}`}
           >
              <Mic size={12} /> <span>VANI</span>
           </button>
        </div>
        <div className="h-3">
           {velocityWarning && <span className="text-[9px] font-bold text-red-500 animate-shake uppercase tracking-widest">{t('jaap_patience')}</span>}
        </div>
      </div>

      <div className="w-full px-2">
           <div className="max-w-4xl mx-auto w-full">
              {mode === 'likhit' ? (
                <div className="space-y-4 animate-fade-in">
                   <div className="relative">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputText}
                        onChange={(e) => processInput(e.target.value)}
                        disabled={isLocked}
                        placeholder={activeMantra.placeholder}
                        className={`w-full text-center py-6 pl-4 pr-32 bg-white dark:bg-stone-800 border-4 border-gray-50 dark:border-stone-700 rounded-[32px] font-hindi font-bold text-templeRed dark:text-saffron shadow-xl focus:border-saffron/30 outline-none transition-all placeholder-gray-200 ${activeMantra?.id === 'mahamantra' ? 'text-sm sm:text-base md:text-lg' : 'text-2xl'}`}
                      />
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-2">
                         <button onClick={() => setIsMuted(!isMuted)} className={`p-2 rounded-full ${isMuted ? 'text-gray-300' : 'text-saffron hover:bg-saffron/10'}`}>{isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
                         <button onClick={() => setInputText('')} className="p-2 text-gray-300 hover:text-red-500 rounded-full"><Eraser size={18} /></button>
                      </div>
                   </div>
                   <div className="bg-white/50 dark:bg-stone-800/50 p-2 rounded-[32px] border border-gray-100 dark:border-stone-700 max-w-2xl mx-auto">
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                         {(activeMantra.keypadKeys || ['र', 'ा', 'म']).map((key, i) => (
                            <button key={i} onClick={() => handleKeyClick(key)} disabled={isLocked} className="h-14 flex items-center justify-center bg-white dark:bg-stone-900 border border-gray-100 dark:border-stone-700 rounded-xl text-xl font-hindi font-bold text-gray-800 dark:text-stone-200 shadow-sm active:scale-90 transition-all">{key}</button>
                         ))}
                         <button onClick={() => handleKeyClick('space')} disabled={isLocked} className="col-span-2 h-14 bg-orange-50 dark:bg-stone-800 border border-saffron/20 rounded-xl flex items-center justify-center text-saffron"><Space size={20} /></button>
                         <button onClick={() => handleKeyClick('backspace')} disabled={isLocked} className="h-14 bg-gray-50 dark:bg-stone-800 border rounded-xl flex items-center justify-center text-gray-400"><Delete size={20} /></button>
                      </div>
                   </div>
                </div>
              ) : mode === 'lekhani' ? (
                <div className="flex flex-col items-center justify-center space-y-4 animate-fade-in">
                    <div className="relative w-full max-w-lg aspect-[3/2] bg-[#fffdf5] dark:bg-stone-900 rounded-[24px] border-4 border-dashed border-orange-200 dark:border-stone-700 shadow-inner overflow-hidden cursor-crosshair">
                        <canvas 
                            ref={canvasRef}
                            onPointerDown={startDrawing}
                            onPointerMove={draw}
                            onPointerUp={stopDrawing}
                            onPointerLeave={stopDrawing}
                            onPointerCancel={stopDrawing}
                            className="w-full h-full"
                            style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
                        />
                        {!hasDrawn && !isDrawing && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                                <span className="text-4xl font-hindi font-bold text-gray-400 dark:text-stone-600">
                                    {activeMantra.hindiLabel}
                                </span>
                            </div>
                        )}
                        <button 
                            onClick={clearCanvas} 
                            className="absolute top-2 right-2 p-2 bg-gray-100 dark:bg-stone-800 text-gray-400 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors"
                            title="Clear"
                        >
                            <Eraser size={16} />
                        </button>
                    </div>
                    
                    <button 
                        onClick={handleArpan}
                        disabled={!hasDrawn}
                        className={`px-8 py-3 rounded-2xl font-black uppercase tracking-widest flex items-center gap-2 shadow-lg transition-all active:scale-95 ${hasDrawn ? 'bg-templeRed dark:bg-saffron text-white dark:text-stone-900 hover:scale-105' : 'bg-gray-200 dark:bg-stone-800 text-gray-400 dark:text-stone-600 cursor-not-allowed'}`}
                    >
                        <Send size={18} /> Arpan (Offer)
                    </button>
                </div>
              ) : mode === 'smaran' ? (
                <div className="flex flex-col items-center justify-center min-h-[340px] animate-fade-in">
                   {/* Edge Tap Mode Toggle - Alternative to volume buttons */}
                   <div className="mb-4 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-stone-800 rounded-2xl border border-gray-100 dark:border-stone-700">
                     <Smartphone size={16} className={volumeButtonEnabled ? 'text-green-500' : 'text-gray-400'} />
                     <span className="text-xs font-bold text-gray-600 dark:text-stone-400">Screen Edge Jaap</span>
                     <button
                       onClick={() => setVolumeButtonEnabled(!volumeButtonEnabled)}
                       data-testid="volume-button-toggle"
                       className={`relative w-12 h-6 rounded-full transition-all duration-300 ${
                         volumeButtonEnabled 
                           ? 'bg-green-500' 
                           : 'bg-gray-300 dark:bg-stone-600'
                       }`}
                     >
                       <div 
                         className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300 ${
                           volumeButtonEnabled ? 'left-7' : 'left-1'
                         }`}
                       />
                     </button>
                   </div>
                   {volumeButtonEnabled && (
                     <p className="text-[10px] text-green-600 dark:text-green-400 font-medium mb-2 text-center max-w-[280px]">
                       Tap anywhere on screen edges (left/right sides) to count jaap
                     </p>
                   )}
                   <button 
                     onMouseDown={handleSmaranTap}
                     onTouchStart={handleSmaranTap}
                     disabled={isLocked}
                     data-testid="jaap-counter"
                     className={`relative w-60 h-60 sm:w-72 sm:h-72 rounded-full flex items-center justify-center transition-all border-8 overflow-hidden touch-none ${isTapping ? 'scale-95 bg-green-50 dark:bg-green-900/10 border-green-200' : 'bg-white dark:bg-stone-800 border-orange-50 dark:border-stone-700 shadow-xl active:scale-95'}`}
                   >
                      {ripples.map(ripple => (
                        <div key={ripple.id} className="absolute bg-saffron/10 rounded-full animate-ping pointer-events-none" style={{ left: ripple.x, top: ripple.y, width: '20px', height: '20px', marginLeft: '-10px', marginTop: '-10px' }} />
                      ))}
                      <div className="relative z-10 flex flex-col items-center">
                        <Fingerprint size={64} className={`transition-all ${isTapping ? 'text-green-500 scale-110' : 'text-saffron'}`} />
                        <span className={`text-3xl font-black font-mono mt-2 ${isTapping ? 'text-green-600 dark:text-green-400' : 'text-gray-800 dark:text-stone-100'}`} data-testid="smaran-count">{currentSessionCount}</span>
                      </div>
                   </button>
                   
                   {/* Screen Edge Tap Zones - Only shown when enabled */}
                   {volumeButtonEnabled && (
                     <>
                       {/* Left Edge Tap Zone */}
                       <div 
                         className="fixed left-0 top-1/4 bottom-1/4 w-16 bg-gradient-to-r from-green-500/20 to-transparent z-40 cursor-pointer active:from-green-500/40 flex items-center justify-start pl-2"
                         onTouchStart={(e) => { e.preventDefault(); handleSmaranTap(e); }}
                         onMouseDown={(e) => { e.preventDefault(); handleSmaranTap(e); }}
                         data-testid="left-edge-tap"
                       >
                         <div className="w-2 h-20 bg-green-500/30 rounded-full" />
                       </div>
                       {/* Right Edge Tap Zone */}
                       <div 
                         className="fixed right-0 top-1/4 bottom-1/4 w-16 bg-gradient-to-l from-green-500/20 to-transparent z-40 cursor-pointer active:from-green-500/40 flex items-center justify-end pr-2"
                         onTouchStart={(e) => { e.preventDefault(); handleSmaranTap(e); }}
                         onMouseDown={(e) => { e.preventDefault(); handleSmaranTap(e); }}
                         data-testid="right-edge-tap"
                       >
                         <div className="w-2 h-20 bg-green-500/30 rounded-full" />
                       </div>
                     </>
                   )}
                </div>
              ) : mode === 'vani' ? (
                <div className="flex flex-col items-center justify-center min-h-[340px] animate-fade-in space-y-4">
                   {/* Browser Support Check */}
                   {!(window as any).SpeechRecognition && !(window as any).webkitSpeechRecognition && vaniMode === 'fast' ? (
                     <div className="text-center p-6 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800 max-w-sm">
                       <MicOff size={40} className="mx-auto mb-3 text-red-400" />
                       <p className="font-bold text-red-600 dark:text-red-400 mb-1">Browser Support नहीं है</p>
                       <p className="text-sm text-red-500">Chrome browser use करें या Accurate Mode चुनें</p>
                     </div>
                   ) : (
                     <>
                       {/* Mode Toggle - disabled during listening */}
                       <div className="w-full max-w-xs" data-testid="vani-mode-toggle">
                         <div className={`flex bg-gray-100 dark:bg-stone-800 rounded-xl p-1 ${isListening ? 'opacity-50 pointer-events-none' : ''}`}>
                           <button
                             onClick={() => { if (!isListening) setVaniMode('fast'); }}
                             className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                               vaniMode === 'fast'
                                 ? 'bg-white dark:bg-stone-700 text-purple-600 dark:text-purple-400 shadow-sm'
                                 : 'text-gray-500 dark:text-stone-400'
                             }`}
                             data-testid="vani-mode-fast"
                           >
                             <Mic size={13} /> Fast
                           </button>
                           <button
                             onClick={() => { if (!isListening) setVaniMode('accurate'); }}
                             className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                               vaniMode === 'accurate'
                                 ? 'bg-white dark:bg-stone-700 text-emerald-600 dark:text-emerald-400 shadow-sm'
                                 : 'text-gray-500 dark:text-stone-400'
                             }`}
                             data-testid="vani-mode-accurate"
                           >
                             <Sparkles size={13} /> Accurate
                           </button>
                         </div>
                         <p className="text-[9px] text-center mt-1 text-gray-400 dark:text-stone-500">
                           {vaniMode === 'fast' ? 'Instant count (Chrome only)' : 'AI-powered (~5 sec delay, all browsers)'}
                         </p>
                       </div>

                       {/* Info Banner - Compact & Highlighted */}
                       <div className="w-full max-w-sm bg-amber-50/90 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl px-3 py-2" data-testid="vani-info-banner">
                         <p className="text-[11px] font-bold text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1"><Radio size={12} /> {t('vani_banner_title')}</p>
                         <p className="text-[10px] text-amber-600/90 dark:text-amber-400/70 leading-relaxed">
                           {t('vani_banner_text')}
                         </p>
                       </div>

                       {/* Mic Button with Waveform */}
                       <div className="relative">
                         {isListening && (
                           <div className="absolute inset-0 flex items-center justify-center">
                             {[...Array(3)].map((_, i) => (
                               <span key={i} className="absolute rounded-full border-2 border-purple-400/40 dark:border-purple-500/30" style={{
                                 width: `${160 + i * 40}px`, height: `${160 + i * 40}px`,
                                 animation: `ping ${1.5 + i * 0.3}s cubic-bezier(0, 0, 0.2, 1) infinite`,
                                 animationDelay: `${i * 0.2}s`
                               }} />
                             ))}
                           </div>
                         )}
                         <button
                           onClick={isListening 
                             ? (vaniMode === 'fast' ? stopVani : stopVaniWhisper) 
                             : (vaniMode === 'fast' ? startVani : startVaniWhisper)}
                           data-testid="vani-mic-button"
                           className={`relative z-10 w-36 h-36 sm:w-44 sm:h-44 rounded-full flex items-center justify-center transition-all border-4 ${
                             isListening 
                               ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-400 dark:border-purple-600 shadow-2xl shadow-purple-300/50 dark:shadow-purple-900/50 scale-105' 
                               : 'bg-white dark:bg-stone-800 border-gray-200 dark:border-stone-700 shadow-xl hover:scale-105 hover:border-purple-300'
                           }`}
                         >
                           {isListening ? (
                             <MicOff size={52} className="text-purple-600 dark:text-purple-400" />
                           ) : (
                             <Mic size={52} className="text-purple-500" />
                           )}
                         </button>
                       </div>

                       <p className="text-sm font-bold text-purple-600 dark:text-purple-400">
                         {isListening ? 'सुन रहा है... बोलें!' : 'माइक टैप करें और बोलें'}
                       </p>

                       {/* Session Counter */}
                       <div className="flex items-center gap-4">
                         <div className="text-center bg-purple-50 dark:bg-purple-900/20 px-6 py-3 rounded-2xl border border-purple-200 dark:border-purple-800">
                           <p className="text-3xl font-black text-purple-700 dark:text-purple-300 font-mono" data-testid="vani-count">{vaniSessionCount}</p>
                           <p className="text-[10px] font-bold text-purple-500 uppercase tracking-widest">Detected</p>
                         </div>
                         <div className="text-center bg-amber-50 dark:bg-amber-900/20 px-6 py-3 rounded-2xl border border-amber-200 dark:border-amber-800">
                           <p className="text-3xl font-black text-amber-700 dark:text-amber-300 font-mono">{Math.floor(vaniSessionCount / 108)}</p>
                           <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Mala</p>
                         </div>
                         {vaniSessionCount > 0 && (
                           <button onClick={resetVaniAll} className="p-3 bg-gray-100 dark:bg-stone-800 rounded-full hover:bg-red-50 hover:text-red-500 transition-colors" title="Reset" data-testid="vani-reset-button">
                             <RotateCcw size={18} />
                           </button>
                         )}
                       </div>

                       {/* Detected Words Visual */}
                       {vaniDetectedWords.length > 0 && (
                         <div className="flex flex-wrap justify-center gap-1.5 max-w-sm">
                           {vaniDetectedWords.map((word, i) => (
                             <span key={i} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-bold animate-fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                               {word}
                             </span>
                           ))}
                         </div>
                       )}

                       {/* Live Transcript - SpeechTexter style */}
                       {vaniTranscript && (
                         <div className="w-full max-w-sm bg-white/80 dark:bg-stone-800/80 backdrop-blur-sm rounded-2xl border border-purple-100 dark:border-stone-700 p-3 max-h-28 overflow-y-auto" data-testid="vani-transcript">
                           <p className="text-[10px] text-purple-400 font-bold uppercase tracking-wider mb-1">Live Transcript</p>
                           <p className="text-xs text-gray-600 dark:text-stone-400 font-hindi leading-relaxed">{vaniTranscript}</p>
                         </div>
                       )}

                       {/* Tips */}
                       <div className="text-center max-w-xs">
                         <p className="text-[10px] text-gray-400 dark:text-stone-500 leading-relaxed">
                           शांत जगह पर बोलें &bull; Chrome browser best है &bull; "{activeMantra?.hindiLabel}" स्पष्ट बोलें &bull; Interim detection ON
                         </p>
                       </div>
                     </>
                   )}
                </div>
              ) : null}

              {/* Sacred Ledger - Swipeable Multi-Mode View */}
              <div className="mt-8 relative max-w-4xl mx-auto flex flex-col">
                 <div className="bg-[#fffdf5] dark:bg-stone-900 rounded-[32px] shadow-2xl border-2 border-gray-100 dark:border-stone-800 flex flex-col relative overflow-hidden transition-all duration-300">
                    
                    {/* Notebook Header with Mode Tabs */}
                    <div className="p-3 px-4 bg-[#fffdf5]/95 dark:bg-stone-900/95 backdrop-blur-sm border-b border-orange-100 dark:border-stone-800 z-30">
                       {/* Mode Tabs for Ledger */}
                       <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                             <Edit3 size={14} className="text-templeRed" />
                             <h4 className="font-serif font-bold text-gray-800 dark:text-stone-300 text-xs">{t('jaap_ledger')}</h4>
                          </div>
                          {pendingVerifications > 0 && (
                            <div className="flex items-center gap-1 text-[8px] font-black uppercase text-orange-500 animate-pulse">
                               <Loader2 size={10} className="animate-spin" /> Verifying...
                            </div>
                          )}
                       </div>
                       
                       {/* Swipeable Mode Tabs */}
                       <div className="flex bg-gray-100/50 dark:bg-stone-800/50 rounded-2xl p-1 gap-1">
                          {(['type', 'lekhani', 'smaran', 'vani'] as const).map((modeTab) => {
                            const historyArr = sessionHistories[selectedMantraId]?.[modeTab] || [];
                            const count = historyArr.length; // Use history length as source of truth
                            const malas = Math.floor(count / 108);
                            const isActive = ledgerViewMode === modeTab;
                            const hasHistory = count > 0;
                            
                            return (
                              <div key={modeTab} className="flex-1 relative">
                                <button
                                  onClick={() => setLedgerViewMode(modeTab)}
                                  data-testid={`ledger-tab-${modeTab}`}
                                  className={`w-full py-2 px-2 rounded-xl text-[9px] font-black uppercase tracking-wide transition-all flex flex-col items-center gap-0.5 ${
                                    isActive 
                                      ? modeTab === 'type' 
                                        ? 'bg-gradient-to-r from-templeRed to-orange-600 text-white shadow-lg' 
                                        : modeTab === 'lekhani'
                                          ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                                          : modeTab === 'smaran'
                                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                                            : 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg'
                                      : 'text-gray-500 dark:text-stone-400 hover:bg-gray-100 dark:hover:bg-stone-700'
                                  }`}
                                >
                                  <span className="flex items-center gap-1">
                                    {modeTab === 'type' && <Edit3 size={10} />}
                                    {modeTab === 'lekhani' && <Pen size={10} />}
                                    {modeTab === 'smaran' && <Fingerprint size={10} />}
                                    {modeTab === 'vani' && <Mic size={10} />}
                                    {modeTab === 'type' ? 'TYPE' : modeTab === 'lekhani' ? 'LEKHANI' : modeTab === 'smaran' ? 'SMARAN' : 'VANI'}
                                  </span>
                                  <span className={`text-[8px] ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                                    {count} • {malas} माला
                                  </span>
                                </button>
                                {/* Download button for each tab */}
                                {hasHistory && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); downloadLedger(modeTab); }}
                                    className={`absolute -top-1 -right-1 p-1 rounded-full shadow-md transition-all hover:scale-110 ${
                                      isActive 
                                        ? 'bg-white text-saffron' 
                                        : 'bg-saffron/10 text-saffron hover:bg-saffron/20'
                                    }`}
                                    title={`Download ${modeLabels[modeTab]} Ledger`}
                                    data-testid={`download-ledger-${modeTab}`}
                                  >
                                    <Download size={10} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                       </div>
                       
                       {/* Current Mode Stats */}
                       <div className="flex items-center justify-center mt-2 px-1">
                          <span className="text-[10px] font-black text-saffron uppercase">
                            {ledgerViewHistory.length} नाम • {Math.floor(ledgerViewHistory.length / 108)} माला ({ledgerViewHistory.length % 108}/108)
                          </span>
                       </div>
                    </div>
                    
                    {/* Ruled Paper Area */}
                    <div className="relative overflow-hidden">
                        {/* Static Double Red Margin Line */}
                        <div className="absolute left-10 top-0 bottom-0 w-0.5 bg-red-400/30 z-20"></div>
                        <div className="absolute left-[44px] top-0 bottom-0 w-0.5 bg-red-400/30 z-20"></div>
                        
                        {/* Scroll Shadows */}
                        <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-black/5 to-transparent z-10 pointer-events-none"></div>
                        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/5 to-transparent z-10 pointer-events-none"></div>

                        {/* Fixed height for exactly 7 lines (Line Height 36px * 7 = 252px) */}
                        <div 
                          ref={ledgerContainerRef} 
                          className="relative h-[252px] overflow-y-auto scroll-smooth no-scrollbar"
                          style={{
                            // Ruled lines background that scrolls with content
                            backgroundImage: 'linear-gradient(transparent 35px, #e5e7eb 35px, #e5e7eb 36px, transparent 36px)',
                            backgroundSize: '100% 36px',
                            backgroundAttachment: 'local', // CRITICAL: Makes lines scroll with text
                            backgroundColor: 'transparent'
                          }}
                        >
                           {/* Padding matches margin lines and line height */}
                           <div className="p-0 pl-14 pr-6 min-h-full font-hindi font-bold leading-[36px] text-[14px] text-templeRed/80 dark:text-saffron/70 text-justify">
                              {ledgerViewHistory.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center opacity-10 py-10" style={{ height: '252px' }}>
                                   <Sparkles size={48} className="text-saffron" />
                                   <p className="text-sm uppercase font-black tracking-widest mt-2">
                                     {ledgerViewMode === 'type' ? 'Type करो' : ledgerViewMode === 'lekhani' ? 'लिखो' : 'Tap करो'}
                                   </p>
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-x-4 pb-1">
                                   {ledgerViewHistory.map((text, i) => (
                                     <span key={i} className="inline-block animate-fade-in hover:scale-110 transition-transform cursor-default select-none">
                                       {text}
                                     </span>
                                   ))}
                                </div>
                              )}
                           </div>
                        </div>

                        {/* Visual Scroll Hint Buttons (Desktop/Manual) */}
                        {ledgerViewHistory.length > 20 && (
                          <div className="absolute right-4 bottom-4 flex flex-col gap-1 z-30">
                            <button 
                              onClick={() => ledgerContainerRef.current?.scrollBy({ top: -72, behavior: 'smooth' })}
                              className="p-1 bg-white/50 dark:bg-stone-800/50 backdrop-blur-md rounded-lg text-gray-400 hover:text-saffron transition-colors"
                            >
                              <ChevronUp size={16} />
                            </button>
                            <button 
                              onClick={() => ledgerContainerRef.current?.scrollBy({ top: 72, behavior: 'smooth' })}
                              className="p-1 bg-white/50 dark:bg-stone-800/50 backdrop-blur-md rounded-lg text-gray-400 hover:text-saffron transition-colors"
                            >
                              <ChevronDown size={16} />
                            </button>
                          </div>
                        )}
                    </div>
                    
                    {/* Bottom Spine Detail */}
                    <div className="h-3 bg-gradient-to-t from-gray-200/40 dark:from-stone-950/40 to-transparent z-30"></div>
                 </div>
              </div>
           </div>
      </div>
    </div>
  );
};


export default JaapInterface;
